/**
 * Analytics Service
 * Handles communication with Spring Boot backend
 */

import postData from "@ca-dmv-radv/data/src/api/post-data";

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const getAnalyticsEndpoint = (applicationType) => {
  const normalizedApplicationType = (applicationType || "dv").toLowerCase();
  const businessProgram =
    normalizedApplicationType === "dv" ? "rid" : normalizedApplicationType;

  return `/${businessProgram}/ui/feature/analytics`;
};

class AnalyticsService {
  constructor() {
    this.queue = [];
    this.isProcessing = false;
  }

  /**
   * Send analytics event
   * @param {Object} eventData - Event metadata
   */
  static async sendEvent(eventData, applicationType) {
    try {
      if (!eventData.sessionId) {
        return; // ← Early return, no API call
      }

      // Convert metadata to JSON string as required
      const payload = {
        navigationData: JSON.stringify(eventData),
        sessionId: eventData.sessionId,
        dmv_uuid: eventData.dmv_uuid,
        businessProgram: applicationType,
      };

      const endpoint = getAnalyticsEndpoint(
        applicationType || eventData?.applicationType
      );

      await AnalyticsService.sendWithRetry(payload, endpoint, MAX_RETRIES);
    } catch (error) {
      // Store failed events in localStorage for later retry
      AnalyticsService.handleFailedEvent(eventData);
    }
  }

  /**
   * Send request with retry logic
   */
  static async sendWithRetry(payload, endpoint, retries = MAX_RETRIES) {
    let lastError;

    for (let attempt = 0; attempt <= retries; attempt += 1) {
      try {
        // postData likely returns data directly, not a Response object
        // eslint-disable-next-line no-await-in-loop
        const response = await postData(endpoint, payload);

        // Return the response data
        return response;
      } catch (error) {
        lastError = error;

        // If this was the last retry, throw the error
        if (attempt === retries) {
          throw error;
        }

        // Wait before retrying (exponential backoff)
        // eslint-disable-next-line no-await-in-loop
        await AnalyticsService.delay(RETRY_DELAY * 2 ** attempt);
      }
    }

    throw lastError;
  }

  /**
   * Handle failed events - store in localStorage for later retry
   */
  static handleFailedEvent(eventData) {
    const failedEvents = JSON.parse(
      localStorage.getItem("failed_analytics_events") || "[]"
    );
    failedEvents.push({
      ...eventData,
      failedAt: new Date().toISOString(),
    });

    // Keep only last 100 failed events to prevent storage bloat
    if (failedEvents.length > 100) {
      failedEvents.shift();
    }

    localStorage.setItem(
      "failed_analytics_events",
      JSON.stringify(failedEvents)
    );
  }

  /**
   * Retry sending failed events from localStorage
   */
  static async retryFailedEvents(applicationType) {
    const failedEvents = JSON.parse(
      localStorage.getItem("failed_analytics_events") || "[]"
    );

    if (failedEvents.length === 0) {
      return;
    }

    const successfulEvents = [];
    const retryPromises = failedEvents.map(async (eventData) => {
      if (!eventData.sessionId) {
        return;
      }
      await AnalyticsService.sendEvent(eventData, applicationType);
      successfulEvents.push(eventData);
    });

    await Promise.all(retryPromises);

    // Remove successfully sent events from storage
    if (successfulEvents.length > 0) {
      const remainingEvents = failedEvents.filter(
        (event) => !successfulEvents.includes(event)
      );
      localStorage.setItem(
        "failed_analytics_events",
        JSON.stringify(remainingEvents)
      );
    }
  }

  /**
   * Utility delay function
   */
  static delay(ms) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  /**
   * Send page unload event (useful for tracking session end)
   */
  static sendBeacon(eventData, applicationType) {
    const payload = {
      metadata: JSON.stringify(eventData),
      eventType: eventData.eventType,
      timestamp: eventData.timestamp,
      userId: eventData.userId,
      sessionId: eventData.sessionId,
    };

    const endpoint = getAnalyticsEndpoint(
      applicationType || eventData?.applicationType
    );

    // Use sendBeacon for reliable delivery on page unload
    const blob = new Blob([JSON.stringify(payload)], {
      type: "application/json",
    });
    navigator.sendBeacon(endpoint, blob);
  }
}

export const analyticsService = AnalyticsService;
